import { createSelector } from 'reselect';

import { ADMIN_LOCK_MESSAGE, LOCKSPEC_LOCK_MESSAGE } from 'constants/locks';
import { defaultWatermarkSelector } from 'redux/modules/display-pref/selectors';
import { KeyTextType } from 'types';
import { selectedOverlaySelector } from '../legacy-selectors';
import { lockCheckerSelector } from '../lockSpecSelectors';

/**
 * Unified lock state for any AE feature.  Field semantics:
 * - `disabled`: the control does not support interaction
 * - `isLocked`: any lock applies; consumer should render the locked UI
 *   (badge, fade, etc.).  Implied false when `disabled` is false. Can set this
 *   to true and `disabled` to false to render the locked UI without disabling
 *   interaciotn
 * - `tooltip`: message to show when the locked UI is rendered; `undefined`
 *   when unlocked.
 */
export type FeatureLockState = {
  disabled: boolean;
  isLocked: boolean;
  tooltip: string | undefined;
};

export const UNLOCKED: FeatureLockState = {
  disabled: false,
  isLocked: false,
  tooltip: undefined,
};

/**
 * Aggregated lock state for the Edit Watermark tile.  Combines the
 * administrator's mandatory-watermark setting
 * (`displayPref.watermark.default`, which keeps the existing click-through
 * to the override-options flow) with the per-project lockSpec watermark lock
 * (which truly disables interaction, mirroring the VTE).  Admin source wins
 * for the message when both apply.
 */
export const watermarkLockStateSelector = createSelector(
  defaultWatermarkSelector,
  lockCheckerSelector,
  (defaultWatermark, lockChecker): FeatureLockState => {
    if (defaultWatermark !== undefined) {
      return { disabled: false, isLocked: true, tooltip: ADMIN_LOCK_MESSAGE };
    }
    if (lockChecker.isFullyLocked('watermark')) {
      return { disabled: true, isLocked: true, tooltip: LOCKSPEC_LOCK_MESSAGE };
    }
    return UNLOCKED;
  },
);

/**
 * Lock state for the captions "Style captions" button.  Driven solely by
 * the per-project lockSpec — captions has no admin display-pref source
 * comparable to watermark's mandatory-watermark setting.  Mirrors the
 * condition used by the VTE's edit-caption-style button.
 */
export const captionsStyleLockStateSelector = createSelector(
  lockCheckerSelector,
  (lockChecker): FeatureLockState => {
    if (!lockChecker.can('captions', 'style')) {
      return { disabled: true, isLocked: true, tooltip: LOCKSPEC_LOCK_MESSAGE };
    }
    return UNLOCKED;
  },
);

/**
 * Lock state for the font-family control of the currently selected text
 * overlay's edit modal.  Only the named title slots (podcastTitle /
 * episodeTitle) carry a `textType`; the per-instance lockSpec entry for that
 * slot decides whether font family is editable.  Free-form overlays have no
 * `textType` and are never locked.
 */
export const selectedOverlayFontFamilyLockStateSelector = createSelector(
  selectedOverlaySelector,
  lockCheckerSelector,
  (overlay, lockChecker): FeatureLockState => {
    const textType: KeyTextType | undefined = overlay?.get('textType');
    if (!textType) {
      return UNLOCKED;
    }
    if (!lockChecker.can('textOverlayInfo', 'style.fontFamily', { textType })) {
      return { disabled: true, isLocked: true, tooltip: LOCKSPEC_LOCK_MESSAGE };
    }
    return UNLOCKED;
  },
);
